import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PortalSwitcher from "@/components/PortalSwitcher";
import CredentialCenter from "@/components/CredentialCenter";
import {
  Megaphone, LogOut, Copy, Download, Loader2, MousePointerClick, UserPlus, FileText,
  Store, Gavel, DollarSign, Clock, Wallet, CheckCircle2, TrendingUp, Save,
} from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
    <div className="flex items-center gap-2 text-slate-400"><Icon className="w-4 h-4" /><span className="text-xs font-medium uppercase tracking-wide">{label}</span></div>
    <div className={`mt-1.5 font-heading text-2xl font-extrabold ${accent || "text-navy"}`}>{value}</div>
  </div>
);

export default function AffiliatePortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [comms, setComms] = useState([]);
  const [form, setForm] = useState({ paypal_email: "", legal_name: "", business_name: "", tax_classification: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/affiliate/me");
      setData(data);
      if (data.profile) setForm({
        paypal_email: data.profile.paypal_email || "", legal_name: data.profile.legal_name || "",
        business_name: data.profile.business_name || "", tax_classification: data.profile.tax_classification || "",
      });
      const c = await api.get("/affiliate/commissions"); setComms(c.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const doLogout = async () => { await logout(); navigate("/"); };

  const copyLink = () => { navigator.clipboard.writeText(data?.referral?.link || ""); toast.success("Referral link copied"); };
  const downloadQr = () => {
    const a = document.createElement("a"); a.href = data.referral.qr; a.download = `crashready-${data.profile.code}.png`; a.click();
  };
  const saveProfile = async () => {
    setSaving(true);
    try { await api.put("/affiliate/profile", form); toast.success("Payout details saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="min-h-screen flex justify-center items-center"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  if (data.status === "none") {
    return <div className="min-h-screen flex items-center justify-center p-6"><div className="text-center"><Megaphone className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-600">No affiliate profile found for this account.</p></div></div>;
  }

  const s = data.stats || {};
  const pending = data.profile?.status === "pending";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/affiliate" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-xl bg-navy flex items-center justify-center"><Megaphone className="w-5 h-5 text-white" strokeWidth={2.2} /></div>
            <span className="font-heading font-extrabold text-lg tracking-tight text-navy">Crash Ready <span className="text-cobalt">Affiliate</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <PortalSwitcher />
            <button onClick={doLogout} data-testid="affiliate-logout" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="affiliate-portal">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Affiliate</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">{data.name}</h1>
        {pending && <div className="mt-3 inline-block text-xs font-semibold text-amber-700 bg-amber-50 rounded-full px-3 py-1.5">Your affiliate account is pending admin approval. Your link works and tracks now.</div>}

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Referral card */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Your Referral Code</div>
            <div className="font-heading text-3xl font-extrabold text-cobalt mt-1" data-testid="affiliate-code">{data.profile.code}</div>
            <div className="mt-3 flex items-center gap-2">
              <input readOnly value={data.referral?.link || ""} className="flex-1 bg-slate-50 border border-slate-200 rounded-full px-3 py-2 text-xs text-slate-600" />
              <button onClick={copyLink} data-testid="copy-link" className="p-2 rounded-lg bg-cobalt-light text-cobalt hover:bg-cobalt hover:text-white transition-all"><Copy className="w-4 h-4" /></button>
            </div>
            {data.referral?.qr && (
              <div className="mt-4 flex items-center gap-4">
                <img src={data.referral.qr} alt="QR" className="h-24 w-24 rounded-xl border border-slate-100" data-testid="affiliate-qr" />
                <button onClick={downloadQr} data-testid="download-qr" className="flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:bg-slate-50"><Download className="w-4 h-4" /> Download QR</button>
              </div>
            )}
          </div>

          {/* Earnings */}
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3">
            <Stat icon={Clock} label="Pending" value={money(s.commission_pending)} accent="text-amber-600" />
            <Stat icon={Wallet} label="Available" value={money(s.commission_available)} accent="text-cobalt" />
            <Stat icon={CheckCircle2} label="Paid" value={money(s.commission_paid)} accent="text-green-600" />
            <Stat icon={TrendingUp} label="Lifetime" value={money(s.lifetime_earnings)} />
          </div>
        </div>

        {/* Performance */}
        <h2 className="font-heading text-lg font-bold text-navy mt-8 mb-3">Referral Performance</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat icon={MousePointerClick} label="Clicks" value={s.clicks || 0} />
          <Stat icon={UserPlus} label="Signups" value={s.signups || 0} />
          <Stat icon={FileText} label="Reports" value={s.reports_started || 0} />
          <Stat icon={FileText} label="Completed" value={s.reports_completed || 0} />
          <Stat icon={Store} label="Marketplace" value={s.cases_marketplace || 0} />
          <Stat icon={Gavel} label="Claimed" value={s.cases_claimed || 0} />
        </div>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* Commissions */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="font-heading font-semibold text-navy mb-3">Commissions</h3>
            {comms.length === 0 ? <p className="text-sm text-slate-400">No commissions yet. Share your link to start earning.</p> : (
              <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
                {comms.map((c) => (
                  <div key={c.commission_id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`commission-${c.commission_id}`}>
                    <div><div className="font-medium text-navy">{money(c.amount)}</div><div className="text-xs text-slate-400 font-mono">{c.case_number}</div></div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${c.status === "paid" ? "bg-green-50 text-green-600" : c.status === "available" ? "bg-cobalt-light text-cobalt" : "bg-amber-50 text-amber-600"}`}>{c.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payout details + history */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h3 className="font-heading font-semibold text-navy mb-3">Payout Details</h3>
            <div className="space-y-2.5">
              <input value={form.paypal_email} onChange={(e) => setForm({ ...form, paypal_email: e.target.value })} data-testid="paypal-email" placeholder="PayPal email" className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
              <input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="legal-name" placeholder="Legal name (for tax)" className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
              <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business name (optional)" className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-cobalt/20 focus:border-cobalt outline-none" />
              <select value={form.tax_classification} onChange={(e) => setForm({ ...form, tax_classification: e.target.value })} className="w-full bg-white border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-cobalt">
                <option value="">Tax classification…</option>
                <option>Individual / Sole Proprietor</option><option>Single-Member LLC</option><option>LLC</option><option>C-Corp</option><option>S-Corp</option><option>Partnership</option>
              </select>
              <button onClick={saveProfile} disabled={saving} data-testid="save-payout" className="flex items-center gap-2 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover transition-all active:scale-95 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
            </div>
            <h4 className="font-semibold text-navy text-sm mt-5 mb-2">Payout History</h4>
            {(!data.payouts || data.payouts.length === 0) ? <p className="text-sm text-slate-400">No payouts yet.</p> : (
              <div className="divide-y divide-slate-50 max-h-40 overflow-y-auto">
                {data.payouts.map((p) => (
                  <div key={p.payout_id} className="flex items-center justify-between py-2 text-sm" data-testid={`payout-${p.payout_id}`}>
                    <div>{money(p.amount)} <span className="text-xs text-slate-400">· {p.method}</span></div>
                    <span className="text-xs font-medium text-slate-500">{p.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8">
          <CredentialCenter role="affiliate" title="Affiliate Credential Center" />
        </div>
      </main>
    </div>
  );
}
